import {
  Grid2,
  Paper,
  Typography,
  Button,
  Box,
  Container,
  Card,
  CardContent,
  useTheme,
  alpha
} from "@mui/material";

import { AccountCircle, Lock, Security, Login as LoginIcon } from "@mui/icons-material";
import ITextBox from "../../controls/iTextbox";
import IToggle from "../../controls/IToogle";
import { useLoginFormConfig } from "./useLoginFormConfig";
import { IForm } from "../../form/IForm";
import IAPIError from "../../controls/iAPIError";

const Login = () => {
  const theme = useTheme();
  const { loginForm, handleThemeChange } = useLoginFormConfig();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
          theme.palette.primary.main,
          0.03
        )} 50%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 2, sm: 3 },
        position: "relative",
        overflow: "hidden"
      }}
    >
      {/* Single main container */}
      <Card
        elevation={32}
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", sm: "95%", md: "900px", lg: "1000px" },
          borderRadius: "32px",
          background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(
            theme.palette.background.paper,
            0.98
          )} 100%)`,
          border: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          position: "relative",
          overflow: "hidden",
          backdropFilter: "blur(10px)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "6px",
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${alpha(
              theme.palette.primary.main,
              0.8
            )} 50%, ${theme.palette.primary.main} 100%)`,
            borderRadius: "32px 32px 0 0"
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "300px",
            height: "300px",
            background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 70%)`,
            borderRadius: "50%",
            zIndex: 0,
            animation: "pulse 4s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 0.3, transform: "translate(-50%, -50%) scale(1)" },
              "50%": { opacity: 0.1, transform: "translate(-50%, -50%) scale(1.1)" }
            }
          }
        }}
      >
        <CardContent sx={{ p: 0, position: "relative", zIndex: 1 }}>
          {/* Floating Money Animations in Empty Spaces */}
          {/* Top area animations */}
          <Box
            sx={{
              position: "absolute",
              top: "5%",
              left: "10%",
              fontSize: { xs: "24px", sm: "32px", md: "40px" },
              color: alpha(theme.palette.primary.main, 0.4),
              animation: "moneyFloatTop 8s ease-in-out infinite",
              zIndex: 1,
              userSelect: "none",
              pointerEvents: "none",
              filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.1))",
              "@keyframes moneyFloatTop": {
                "0%, 100%": { transform: "translateY(0px) rotate(0deg) scale(1)", opacity: 0.6 },
                "50%": { transform: "translateY(-20px) rotate(180deg) scale(1.2)", opacity: 0.9 }
              }
            }}
          >
            $
          </Box>

          <Box
            sx={{
              position: "absolute",
              top: "8%",
              right: "15%",
              fontSize: { xs: "20px", sm: "28px", md: "36px" },
              color: alpha(theme.palette.primary.main, 0.3),
              animation: "moneyFloatTopRight 6s ease-in-out infinite",
              animationDelay: "1s",
              zIndex: 1,
              userSelect: "none",
              pointerEvents: "none",
              filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.1))",
              "@keyframes moneyFloatTopRight": {
                "0%, 100%": { transform: "translateX(0px) rotate(0deg) scale(1)", opacity: 0.5 },
                "50%": { transform: "translateX(-15px) rotate(-180deg) scale(1.1)", opacity: 0.8 }
              }
            }}
          >
            $
          </Box>

          {/* Bottom area animations */}
          <Box
            sx={{
              position: "absolute",
              bottom: "8%",
              left: "8%",
              fontSize: { xs: "22px", sm: "30px", md: "38px" },
              color: alpha(theme.palette.primary.main, 0.35),
              animation: "moneyFloatBottom 7s ease-in-out infinite",
              animationDelay: "2s",
              zIndex: 1,
              userSelect: "none",
              pointerEvents: "none",
              filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.1))",
              "@keyframes moneyFloatBottom": {
                "0%, 100%": { transform: "translateY(0px) rotate(0deg) scale(1)", opacity: 0.6 },
                "50%": { transform: "translateY(15px) rotate(180deg) scale(1.3)", opacity: 0.9 }
              }
            }}
          >
            💸
          </Box>

          <Box
            sx={{
              position: "absolute",
              bottom: "5%",
              right: "12%",
              fontSize: { xs: "18px", sm: "26px", md: "34px" },
              color: alpha(theme.palette.primary.main, 0.4),
              animation: "moneyFloatBottomRight 5s ease-in-out infinite",
              animationDelay: "3s",
              zIndex: 1,
              userSelect: "none",
              pointerEvents: "none",
              filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.1))",
              "@keyframes moneyFloatBottomRight": {
                "0%, 100%": { transform: "scale(1) rotate(0deg)", opacity: 0.5 },
                "50%": { transform: "scale(1.2) rotate(180deg)", opacity: 0.8 }
              }
            }}
          >
            💎
          </Box>

          {/* Center area animations (only on desktop where there's space) */}
          <Box
            sx={{
              position: "absolute",
              top: "45%",
              left: "5%",
              fontSize: { xs: "16px", sm: "24px", md: "32px" },
              color: alpha(theme.palette.primary.main, 0.3),
              animation: "moneyFloatCenter 9s ease-in-out infinite",
              animationDelay: "4s",
              zIndex: 1,
              userSelect: "none",
              pointerEvents: "none",
              filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.1))",
              display: { xs: "none", md: "block" },
              "@keyframes moneyFloatCenter": {
                "0%, 100%": { transform: "translate(0px, 0px) rotate(0deg) scale(1)", opacity: 0.4 },
                "25%": { transform: "translate(-10px, -15px) rotate(90deg) scale(1.1)", opacity: 0.7 },
                "75%": { transform: "translate(10px, 10px) rotate(270deg) scale(0.9)", opacity: 0.6 }
              }
            }}
          >
            $
          </Box>

          <Box
            sx={{
              position: "absolute",
              top: "55%",
              right: "5%",
              fontSize: { xs: "16px", sm: "24px", md: "32px" },
              color: alpha(theme.palette.primary.main, 0.25),
              animation: "moneyFloatCenterRight 6s ease-in-out infinite",
              animationDelay: "5s",
              zIndex: 1,
              userSelect: "none",
              pointerEvents: "none",
              filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.1))",
              display: { xs: "none", md: "block" },
              "@keyframes moneyFloatCenterRight": {
                "0%, 100%": { transform: "translateY(0px) rotate(0deg) scale(1)", opacity: 0.3 },
                "50%": { transform: "translateY(-12px) rotate(180deg) scale(1.2)", opacity: 0.7 }
              }
            }}
          >
            $
          </Box>

          <Grid2 container sx={{ minHeight: { xs: "auto", md: "600px" } }}>
            {/* Image Section - Left on desktop, top on mobile */}
            <Grid2
              xs={12}
              md={5}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                order: { xs: 2, md: 1 },
                p: { xs: 2, md: 3 },
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, transparent 100%)`,
                position: "relative"
              }}
            >
              {/* Enhanced Money-themed decorative elements */}
              <Box
                sx={{
                  position: "absolute",
                  top: "20px",
                  right: "20px",
                  fontSize: "40px",
                  color: alpha(theme.palette.primary.main, 0.5),
                  animation: "moneyRotate 8s linear infinite",
                  filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.2))",
                  "@keyframes moneyRotate": {
                    "0%": { transform: "rotate(0deg) scale(1)" },
                    "50%": { transform: "rotate(180deg) scale(1.3)" },
                    "100%": { transform: "rotate(360deg) scale(1)" }
                  }
                }}
              >
                💰
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: "20px",
                  left: "20px",
                  fontSize: "32px",
                  color: alpha(theme.palette.primary.main, 0.4),
                  animation: "moneyBounce 4s ease-in-out infinite",
                  filter: "drop-shadow(0 3px 10px rgba(0,0,0,0.15))",
                  "@keyframes moneyBounce": {
                    "0%, 100%": { transform: "translateY(0) scale(1)" },
                    "50%": { transform: "translateY(-20px) scale(1.4)" }
                  }
                }}
              >
            $
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: "10px",
                  fontSize: "28px",
                  color: alpha(theme.palette.primary.main, 0.3),
                  animation: "moneyFloat 6s ease-in-out infinite",
                  filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.1))",
                  "@keyframes moneyFloat": {
                    "0%, 100%": { transform: "translateX(0) rotate(0deg)" },
                    "50%": { transform: "translateX(-15px) rotate(180deg)" }
                  }
                }}
              >
                💸
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: "30%",
                  left: "10px",
                  fontSize: "24px",
                  color: alpha(theme.palette.primary.main, 0.35),
                  animation: "moneySpin 5s linear infinite",
                  filter: "drop-shadow(0 3px 10px rgba(0,0,0,0.2))",
                  "@keyframes moneySpin": {
                    "0%": { transform: "rotate(0deg) scale(0.8)" },
                    "50%": { transform: "rotate(180deg) scale(1.3)" },
                    "100%": { transform: "rotate(360deg) scale(0.8)" }
                  }
                }}
              >
                💎
              </Box>

              {/* Additional floating money in image section */}
              <Box
                sx={{
                  position: "absolute",
                  top: "30%",
                  left: "15px",
                  fontSize: "26px",
                  color: alpha(theme.palette.primary.main, 0.4),
                  animation: "moneyDrift 7s ease-in-out infinite",
                  filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.1))",
                  "@keyframes moneyDrift": {
                    "0%, 100%": { transform: "translateY(0) rotate(0deg) scale(1)" },
                    "33%": { transform: "translateY(-10px) rotate(120deg) scale(1.2)" },
                    "66%": { transform: "translateY(5px) rotate(240deg) scale(0.9)" }
                  }
                }}
              >
            $
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: "10%",
                  right: "15px",
                  fontSize: "22px",
                  color: alpha(theme.palette.primary.main, 0.3),
                  animation: "moneyGlow 5s ease-in-out infinite",
                  filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.15))",
                  "@keyframes moneyGlow": {
                    "0%, 100%": { transform: "scale(1)" },
                    "50%": { transform: "scale(1.2)" }
                  }
                }}
              >
                💸
              </Box>

              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  maxWidth: "400px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: "350px",
                    filter: "drop-shadow(0 15px 35px rgba(0,0,0,0.1))",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: "-20px",
                      left: "-20px",
                      right: "-20px",
                      bottom: "-20px",
                      background: `conic-gradient(from 0deg, ${alpha(
                        theme.palette.primary.main,
                        0.1
                      )}, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.1)})`,
                      borderRadius: "50%",
                      zIndex: -1,
                      animation: "spin 8s linear infinite",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" }
                      }
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: "auto",
                      background: `linear-gradient(135deg, ${alpha(
                        theme.palette.background.paper,
                        0.9
                      )} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                      borderRadius: "20px",
                      padding: "20px",
                      border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      backdropFilter: "blur(10px)",
                      zIndex: 1
                    }}
                  >
                    <img
                      src="/assets/images/Login.png"
                      alt="ITrade4You Partner"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                        position: "relative",
                        zIndex: 1,
                        borderRadius: "12px"
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid2>

            {/* Divider line for desktop */}
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                width: "1px",
                background: `linear-gradient(180deg, transparent 0%, ${alpha(
                  theme.palette.primary.main,
                  0.2
                )} 50%, transparent 100%)`,
                position: "absolute",
                left: "50%",
                top: "10%",
                bottom: "10%",
                transform: "translateX(-50%)",
                zIndex: 1
              }}
            />

            {/* Login Form Section - Right on desktop, top on mobile */}
            <Grid2
              xs={12}
              md={7}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                order: { xs: 1, md: 2 },
                p: { xs: 2, md: 3 }
              }}
            >
              <Box sx={{ width: "100%", maxWidth: "400px" }}>
                {/* Header Section */}
                <Box sx={{ textAlign: "center", mb: 4 }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: "bold",
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${alpha(
                        theme.palette.primary.main,
                        0.7
                      )} 100%)`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      mb: 1,
                      textShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.1)}`
                    }}
                  >
                    ITrade4You Partner
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 400,
                      lineHeight: 1.4,
                      opacity: 0.8
                    }}
                  >
                    Trusted Partner to Eliminate Trading Stress and Deliver Profits
                  </Typography>
                </Box>

                {/* Form Section */}
                <Box sx={{ mb: 3 }}>
                  <IForm form={loginForm}>
                    <Box sx={{ mb: 2.5 }}>
                      <ITextBox
                        model="userName"
                        label="User Name"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "16px",
                            background: alpha(theme.palette.background.paper, 0.8),
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.primary.main,
                              borderWidth: "2px"
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.primary.main,
                              borderWidth: "2px"
                            }
                          }
                        }}
                        icon={<AccountCircle />}
                      />
                    </Box>

                    <Box sx={{ mb: 2.5 }}>
                      <ITextBox
                        model="password"
                        label="Password"
                        type="password"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "16px",
                            background: alpha(theme.palette.background.paper, 0.8),
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.primary.main,
                              borderWidth: "2px"
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.primary.main,
                              borderWidth: "2px"
                            }
                          }
                        }}
                        icon={<Lock />}
                      />
                    </Box>

                    <Box sx={{ mb: 2.5 }}>
                      <ITextBox
                        model="totp"
                        autoFocus
                        label="TOTP"
                        type="password"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "16px",
                            background: alpha(theme.palette.background.paper, 0.8),
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.primary.main,
                              borderWidth: "2px"
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: theme.palette.primary.main,
                              borderWidth: "2px"
                            }
                          }
                        }}
                        icon={<Security />}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <IToggle
                        model="virtual"
                        label="Trading Mode"
                        options={[
                          { key: "PT", value: "Paper Trading" },
                          { key: "LT", value: "Live Trading" }
                        ]}
                        onChange={(e, value) => {
                          if (value !== null) {
                            handleThemeChange(value);
                          }
                        }}
                      />
                    </Box>

                    <IAPIError />

                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={loginForm.handleSubmit}
                      startIcon={<LoginIcon sx={{ fontSize: "1.2rem" }} />}
                      sx={{
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: "600",
                        borderRadius: "12px",
                        textTransform: "none",
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${alpha(
                          theme.palette.primary.main,
                          0.8
                        )} 100%)`,
                        boxShadow: `0 6px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: "-100%",
                          width: "100%",
                          height: "100%",
                          background: `linear-gradient(90deg, transparent, ${alpha("#fff", 0.2)}, transparent)`,
                          transition: "left 0.5s"
                        },
                        "&:hover": {
                          background: `linear-gradient(45deg, ${alpha(
                            theme.palette.primary.main,
                            0.9
                          )} 30%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.35)}`,
                          transform: "translateY(-2px)",
                          "&::before": {
                            left: "100%"
                          }
                        },
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                      }}
                    >
                      Sign In
                    </Button>
                  </IForm>
                </Box>
              </Box>
            </Grid2>
          </Grid2>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
